/**
 * Plan contra realidad, unidad por unidad.
 *
 * `estimated_effort_hours`, `consumed_effort_hours`, `planned_end` y el cierre del reloj
 * existían desde siempre y **ninguna pantalla los enfrentaba**: el dato estaba y la pregunta
 * no se podía hacer.
 *
 * Es tabla antes que gráfico a propósito. La pregunta de un gerente acá no es "¿cómo venimos
 * en promedio?" sino "¿cuál se fue de mano?", y eso es una fila con nombre.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { KpiCard } from '@/components/charts';

const COLUMNAS = [
  { id: 'desvio_pct', label: 'Desvío esfuerzo', ancho: 'w-28' },
  { id: 'desvio_dias', label: 'Desvío fecha', ancho: 'w-28' },
];

function Horas({ valor }) {
  if (valor == null) return <span className="text-zinc-300">—</span>;
  return <span>{valor}h</span>;
}

function Desvio({ valor, sufijo }) {
  if (valor == null) {
    return <span className="text-zinc-400 text-[10px]">sin plan</span>;
  }
  const signo = valor > 0 ? '+' : '';
  // Pasarse es rojo y quedarse corto es ámbar, no verde: quedarse a la mitad de lo estimado
  // también es planificar mal, sólo que se nota menos.
  const tono = valor > 0 ? 'text-brand-danger' : valor < 0 ? 'text-amber-600' : 'text-zinc-500';
  return <span className={`font-semibold ${tono}`}>{signo}{valor}{sufijo}</span>;
}

export default function PanelDesvio({ datos, loading, unidadPlural = 'Tickets' }) {
  const [orden, setOrden] = useState('desvio_pct');
  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
      </div>
    );
  }
  const resumen = datos?.resumen || {};
  const filas = [...(datos?.unidades || [])].sort(
    (a, b) => Math.abs(b[orden] ?? 0) - Math.abs(a[orden] ?? 0)
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard
          label="Entregado en fecha"
          value={resumen.entregado_en_fecha_pct == null ? '—' : `${resumen.entregado_en_fecha_pct}%`}
          sub={`sobre ${resumen.terminadas_con_fecha || 0} terminadas con fecha`}
        />
        <KpiCard
          label="Desvío de esfuerzo"
          value={resumen.desvio_esfuerzo_pct_mediano == null ? '—' : `${resumen.desvio_esfuerzo_pct_mediano}%`}
          sub="mediana, sólo lo estimado"
          danger={(resumen.desvio_esfuerzo_pct_mediano || 0) > 0}
        />
        <KpiCard
          label="En curso vencidas"
          value={resumen.en_curso_vencidas ?? '—'}
          sub="ya pasaron su fecha"
          danger={(resumen.en_curso_vencidas || 0) > 0}
        />
        {/* Las dos que también son información: no se puede medir el desvío de lo que nadie
            planificó, y esconderlas haría creer que se midió todo. */}
        <KpiCard label="Sin estimación" value={resumen.sin_estimacion ?? '—'} sub="no se puede medir el desvío" />
        <KpiCard label="Sin compromiso" value={resumen.sin_fecha_comprometida ?? '—'} sub="no hay contra qué comparar el atraso" />
      </div>

      <Card className="rounded-sm border-brand-border" data-testid="executive-desvio">
        <CardHeader className="pb-2 border-b border-brand-border py-2">
          <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em]">
            {unidadPlural} por desvío
          </CardTitle>
          <p className="text-[10px] text-zinc-500 font-normal normal-case">
            Estimado contra consumido y fecha comprometida contra cierre real. Ordená por la
            columna que te interese. <span className="text-zinc-400">El filtro de estado no se
            aplica acá: el desvío entregado se mide sobre lo que terminó.</span>
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {!filas.length ? (
            <p className="text-[11px] text-zinc-400 p-3">Sin unidades para estos filtros.</p>
          ) : (
            <div className="max-h-[32rem] overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-zinc-50 border-b border-brand-border sticky top-0">
                  <tr>
                    <th className="text-left p-2">Unidad</th>
                    <th className="text-right p-2 w-20">Estimado</th>
                    <th className="text-right p-2 w-20">Consumido</th>
                    {COLUMNAS.map((c) => (
                      <th key={c.id} className={`text-right p-2 ${c.ancho}`}>
                        <button
                          onClick={() => setOrden(c.id)}
                          className={`hover:text-brand-primary ${orden === c.id ? 'text-brand-primary font-semibold' : ''}`}
                        >
                          {c.label}{orden === c.id ? ' ↓' : ''}
                        </button>
                      </th>
                    ))}
                    {/* «Comprometida» es `fecha_hasta`: lo que se le prometio a quien pidio. Hasta
                        el 2026-10-01 esta columna mostraba `planned_end` —el plan tipeado a mano— con
                        el mismo rotulo, asi que esta pestaña y la de Resumen contestaban "¿llegamos a
                        lo prometido?" con dos definiciones distintas. El plan tiene su columna. */}
                    <th className="text-left p-2 w-28">Comprometida</th>
                    <th className="text-left p-2 w-28">Plan</th>
                    <th className="text-left p-2 w-32">Responsable</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f) => (
                    <tr key={f.ticket_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="p-2">
                        <span className="font-mono text-[10px] text-brand-primary">{f.ticket_id}</span>
                        <span className="text-zinc-600 ml-2">{f.title}</span>
                        {!f.terminada && (
                          <span className="ml-2 text-[9px] text-zinc-400 uppercase">en curso</span>
                        )}
                      </td>
                      <td className="p-2 text-right"><Horas valor={f.estimado_horas} /></td>
                      <td className="p-2 text-right"><Horas valor={f.consumido_horas} /></td>
                      <td className="p-2 text-right"><Desvio valor={f.desvio_pct} sufijo="%" /></td>
                      <td className="p-2 text-right"><Desvio valor={f.desvio_dias} sufijo="d" /></td>
                      {/* Los cuatro nulos de `relojes` no son lo mismo, y se dicen: un intent que
                          no promete cierre es legitimo; un horario roto es configuracion a revisar.
                          Una celda vacia los confunde a los cuatro. */}
                      <td className="p-2 text-zinc-500">
                        {f.fecha_comprometida
                          ? String(f.fecha_comprometida).slice(0, 10)
                          : (f.fecha_comprometida_origen === 'sin_sla' ? 'sin compromiso'
                            : f.fecha_comprometida_origen === 'no_calculable' ? 'no calculable'
                            : '—')}
                      </td>
                      <td className="p-2 text-zinc-500">
                        {f.fecha_plan ? String(f.fecha_plan).slice(0, 10) : '—'}
                      </td>
                      <td className="p-2 text-zinc-500 truncate">{f.responsable || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
